import { Condition, OrderDirection, Paginator } from '@decaf-ts/core';
import { composedFromCreateUpdate, ComposedFromMetadata, OperationKeys, PrimaryKeyType } from '@decaf-ts/db-decorators';
import { Model } from '@decaf-ts/decorator-validation';
import {
  ActionRoles,
  DecafRepository,
  FileUploadComponent,
  getLogger,
  getModelAndRepository,
  getNgxModalComponent,
  ICrudFormEvent,
  IFilterQuery,
  injectRepository,
  KeyValue,
  ListComponent,
  NgxComponentDirective,
  presentModalConfirm,
  SelectOption,
  TableComponent,
} from '@decaf-ts/for-angular';
import { AxiosFlavour } from '@decaf-ts/for-http';
import {
  ComponentEventNames,
  HTML5InputTypes,
  uielement,
  uihandlers,
  uilistmodel,
  uitablecol,
} from '@decaf-ts/ui-decorators';
import {
  Batch,
  getDoucumentOptions,
  getLanguageOptions,
  getMarketOptions,
  Leaflet,
  LeafletFile,
  LocaleHelper,
} from '@pharmaledgerassoc/ptp-toolkit/shared';
import { getNgxToastComponent } from 'src/app/utils/NgxToastComponent';
import { AppLeafletItemComponent } from '../components/leaflet-item/leaflet-item.component';
import { NgxEwBasePage } from '../utils/NgxEwBasePage';
import { EwBaseHandler } from './EwBaseHandler';
import { LeafletFileItem } from '../utils/types';

// change list item default component
uilistmodel('app-leaflet-item', { icon: 'ti-file-barcode', translatable: false })(Leaflet);

export function getLeafletEpiProps(filterBy: 'productCode' | 'batchNumber' = 'productCode') {
  return {
    showSearchbar: false,
    title: 'Documents',
    operation: OperationKeys.READ,
    operations: [OperationKeys.READ],
    showRefresher: false,
    readonly: true,
    route: 'leaflets',
    icon: 'ti-file-barcode',
    model: getLeafletModelDecoration(),
    filterBy: 'productCode',
    // propsMapperFn: {
    //   mapper: (instance: ListComponent) => {
    //     const { operation } = instance;
    //     if ([OperationKeys.READ, OperationKeys.DELETE].includes(operation as OperationKeys)) {

    //       // return {
    //       //   description: 'leafletType',
    //       //   title: 'lang',
    //       //   info: 'batchNumber',
    //       //   subinfo: 'epiMarket',
    //       // };
    //     }
    //   },
    // },
  };
}

export function getLeafletModelDecoration(): Leaflet {
  const model = new Leaflet();
  uihandlers({
    [ComponentEventNames.Submit]: LeafletHandler,
    [ComponentEventNames.Render]: LeafletHandler,
  })(Leaflet);

  uitablecol(1)(Leaflet, 'batchNumber');
  uitablecol(2, async (instance: ListComponent, prop: keyof Leaflet, value: string) => {
    return LeafletHandler.getDocumentType(instance, prop, value);
  })(Leaflet, 'leafletType');

  uitablecol(3, async (instance: ListComponent, prop: keyof Leaflet, value: string) => {
    return LeafletHandler.getLanguage(instance, prop, value);
  })(Leaflet, 'lang');
  uielement('ngx-decaf-crud-field', {
    label: 'leaflet.lang.label',
    placeholder: 'leaflet.lang.placeholder',
    type: HTML5InputTypes.SELECT,
    value: 'en',
    options: getLanguageOptions().map((l) => ({
      name: l.text,
      code: l.value,
    })),
    optionsMapper: (item: { name: string; code: string }) => ({
      text: item.name,
      value: item.code.toLowerCase(),
      disabled: false,
      selected: item.code.toLowerCase() === 'en',
    }),
    startEmpty: false,
    translatable: false,
  })(Leaflet, 'lang');

  uielement('app-select-field', {
    label: 'leaflet.market.label',
    placeholder: 'leaflet.market.placeholder',
    translatable: false,
    type: HTML5InputTypes.SELECT,
    optionsMapper: (item: { name: string; code: string }) => ({
      text: item.name,
      value: item.code.toLowerCase(),
      disabled: false,
    }),
    options: LocaleHelper.getMarkets().map((item) => ({
      ...item,
      selected: item.code === 'us',
    })),
  })(Leaflet, 'epiMarket');

  uitablecol(4, async (instance: ListComponent, prop: keyof Leaflet, value: string) => {
    return LeafletHandler.getMarket(instance, prop, value);
  })(Leaflet, 'epiMarket');

  uielement('ngx-decaf-file-upload', {
    label: 'product.xmlFileContent.label',
    placeholder: 'product.xmlFileContent.placeholder',
    enableDirectoryMode: true,
    showIcon: false,
    size: 'small',
    position: 'left',
    required: true,
    maxFileSize: 10,
    valueType: 'files',
    subType: 'text',
    previewHandler: async (instance: FileUploadComponent) => await LeafletHandler.handleDocumentPreview(instance),
    accept: ['image/*', '.xml'],
  })(Leaflet, 'xmlFileContent');

  return model;
}

export class LeafletHandler extends EwBaseHandler {
  static files: LeafletFileItem[];

  static listComponent: ListComponent;

  static async getModel(instance: NgxEwBasePage): Promise<Leaflet> {
    await this.beforeRender(instance, Leaflet);
    return getLeafletModelDecoration();
  }

  override async render(instance: TableComponent, prop: string, value: string, args: any): Promise<string | void> {
    if (!LeafletHandler.instance) {
      LeafletHandler.instance = instance;
    }
    const { operation, type } = instance;
    if ((instance.model as Leaflet)?.xmlFileContent) {
      LeafletHandler.model = instance.model as Leaflet;
    } else {
      const productCode = (instance.modelId as string)?.split(':')?.[0];
      LeafletHandler.model = Model.build({ productCode }, Leaflet.name) as Leaflet;
    }

    if (operation === OperationKeys.READ) {
      if (prop === 'lang') {
        instance.value = getLanguageOptions().find((item: SelectOption) => value === item.value)?.text || '';
      }
      if (prop === 'epiMarket' && instance.value) {
        instance.value = getMarketOptions().find((item: SelectOption) => value === item.value)?.text || '';
      }
    }

    if (prop === 'Leaflet') {
      const modelId = instance.modelId as string | null;

      if (!modelId) {
        instance.searchValue = LeafletHandler.getSearchOptions(instance.filterBy, '', 'Contains');
      } else {
        instance['parseResult'] = LeafletHandler.parseSearchResult;
        instance.searchValue = LeafletHandler.getSearchOptions(
          instance.filterBy,
          modelId.split(':')[0] as string,
          'Contains'
        );
      }
    }
    if (prop === 'xmlFileContent') {
      if (instance.operation !== OperationKeys.CREATE) {
        await LeafletHandler.getDocumentFiles(instance.modelId as string, instance as unknown as FileUploadComponent);
      }
    }
    if (prop === 'batchNumber' && value) {
      if (instance?.headers)
        instance.headers = instance.headers.map((header) => (header === 'batchNumber' ? 'batchCol' : header));
      if (!operation) {
        return value;
      }
      // falback to list item position if has type is paginated or infinite and value is not mapped to avoid empty column
      return 'info';
    }
  }

  override async handle<C extends NgxComponentDirective>(
    event: ICrudFormEvent,
    data: KeyValue,
    instance?: C
  ): Promise<void> {
    const repo = getModelAndRepository(this.model as Model);
    let operation = this.operation;
    let success = false;
    let result = null;
    let uid = this.modelId;
    let error;

    LeafletHandler.loadingMessage = await this.translate(LeafletHandler.loadingMessage, { '0': '' });
    if (repo) {
      try {
        await LeafletHandler.showLoading();
        const { repository, pk } = repo;
        let model = Model.build(data as Partial<Leaflet>, Leaflet.name) as Leaflet;
        const partialModel = model as Partial<Leaflet>;
        if (!partialModel.epiMarket?.length || partialModel.batchNumber?.length) {
          delete partialModel.epiMarket;
          model = partialModel as Leaflet;
        }
        const composedMetadata = Model.composed(model as Model, pk as keyof Model);
        composedFromCreateUpdate.call(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this as any,
          new Leaflet(),
          composedMetadata as ComposedFromMetadata,
          pk as keyof Model,
          model as Model
        );
        uid = model[pk as keyof Leaflet] as PrimaryKeyType;
        let leaflet: Leaflet | null = null;
        switch (operation) {
          case OperationKeys.CREATE:
            {
              try {
                leaflet = (await repository.read(uid as string)) as Leaflet;
              } catch (error) {
                this.log.for(this.handle).debug(`Leaflet with id ${uid} not found, creating new one.`);
              }

              if (leaflet) {
                operation = OperationKeys.UPDATE;
                const mustUpdate = await LeafletHandler.showConfirmationModal(repository.class.name);
                if (mustUpdate) {
                  await LeafletHandler.showLoading();
                  result = await LeafletHandler.parseFileContent(Object.assign(leaflet, data), operation);
                  result = await repository.update(result);
                }
              } else {
                result = await LeafletHandler.parseFileContent(model as Model);
                result = await repository.create(result);
              }
            }
            break;
          case OperationKeys.READ:
          case OperationKeys.DELETE:
            {
              result = await repository.delete(uid, data);
            }
            break;
        }
        if (result) {
          success = true;
        }
      } catch (e: unknown) {
        error = e;
        this.log.for(this.handle).error(`Error on processing ${operation}: ${(error as Error)?.message}`);
      }
    }

    if (success) {
      await LeafletHandler.handleRedirect(instance as NgxComponentDirective, `leaflets`);
    }
    if (LeafletHandler.loading.isVisible()) {
      await LeafletHandler.loading.remove();
    }
    if (result) {
      const options = {
        color: success ? 'dark' : 'danger',
        message: await this.translate(`operations.${operation}.${success ? 'success' : 'error'}`, {
          '0': this.pk,
          '1': uid,
        }),
      };
      if (!success) {
        await LeafletHandler.handleError(instance as NgxComponentDirective, `leaflets`, error as Error);
      }
      const toast = getNgxToastComponent(options);
      await toast.show(options);
    }
  }

  static async showConfirmationModal(modelName: string): Promise<boolean> {
    const loading = this.loading;
    if (loading.isVisible()) {
      await loading.remove();
    }
    const locale = modelName.toLowerCase();
    const modal = await getNgxModalComponent({
      tag: 'app-modal-diffs',
      expandable: false,
      title: `${locale}.diffs.confirmation.title`,
      className: 'dcf-modal-cofirm dcf-modal-leaflet',
      //  headerTransparent: true,
      globals: {
        message: `${locale}.diffs.confirmation.message`,
        locale,
      },
    });
    await modal.present();
    const { role } = await modal.onDidDismiss();
    if (role === ActionRoles.cancel) {
      return false;
    }

    return true;
  }

  static async handleDelete(instance: AppLeafletItemComponent): Promise<boolean> {
    const item = await instance.translate('leaflet.delete');
    const { repository } = getModelAndRepository('Leaflet') || {};
    if (!repository) {
      return false;
    }
    const modal = await presentModalConfirm(
      {
        data: {
          item,
          pk: instance.pk,
          uid: instance.uid as string,
        },
      },
      ActionRoles.delete
    );
    await modal.present();
    const { role } = await modal.onDidDismiss();
    if (role !== ActionRoles.confirm) {
      return false;
    }
    let error = false;
    try {
      await LeafletHandler.showLoading((await instance.translate('leaflet.deleting')) as string);
      await repository.delete(instance.uid as string);
    } catch (err: unknown) {
      const logger = getLogger('LeafletHandler');
      logger.for(this.handleDelete).error(`Error deleting leaflet: ${(err as Error)?.message} || ${String(err)}`);
      error = true;
    } finally {
      if (this.loading.isVisible()) {
        await this.loading.remove();
      }
    }
    return !error;
  }

  static async handleDocumentPreview(instance: Partial<FileUploadComponent>, args?: any): Promise<void> {
    const { operation, value, _data } = instance || {};
    const loading = this.loading;
    await loading.show();
    const modal = await getNgxModalComponent({
      tag: 'app-leaflet-preview',
      // title: 'ePI Preview',
      headerTransparent: true,
      expandable: false,
      borders: false,
      showHeader: false,
      uid: 'leaflet-preview-modal',
      globals: {
        value,
        data: _data,
        model: this.model,
        operation: operation || OperationKeys.READ,
        language: (LeafletHandler.model as Leaflet)?.lang || 'en',
      },
    });
    if (loading.isVisible()) {
      await loading.remove();
    }
    await modal.present();
  }

  static async readFilesAndShowPreview(leafletId: string): Promise<void> {
    return this.handleDocumentPreview(await this.getDocumentFiles(leafletId));
  }

  static async getDocumentFiles(
    leafletId: string,
    instance?: FileUploadComponent
  ): Promise<{ _data: KeyValue[]; value: string[] }> {
    function parseFileSource(file: string, fileName: string): string {
      if (/^data:[^;]+;base64,/.test(file)) {
        return file;
      }
      const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
      let type = 'text/xml';
      switch (ext) {
        case 'jpg':
        case 'jpeg':
          type = 'image/jpeg';
          break;
        case 'png':
          type = 'image/png';
          break;
        case 'gif':
          type = 'image/gif';
          break;
        case 'webp':
          type = 'image/webp';
          break;
        case 'svg':
          type = 'image/svg+xml';
          break;
      }
      return `data:${type};base64,${file}`;
    }
    const repository = injectRepository<LeafletFile>(LeafletFile, AxiosFlavour) as DecafRepository<LeafletFile>;
    const files = [];
    if (repository) {
      const query = await repository
        .select()
        .where(Condition.attr('leafletId' as keyof LeafletFile).eq(leafletId))
        .execute();
      if (query?.length) {
        for (const file of query as LeafletFile[]) {
          files.push(file);
        }
      }
    }
    const xmlFIle = files.find((file) => {
      const { fileName, fileContent } = file;
      if (fileName.includes('xml') || fileContent.includes('xml')) {
        return file;
      }
    });
    if (instance) {
      instance.label = (await instance.translate('leaflet.file.label')) as string;
      if (files?.length) {
        instance._data = files;
        instance.name = 'Click to preview file';
        instance.setValue(xmlFIle ? [xmlFIle.fileContent] : []);
        instance.parseValue();
      }
    }

    return { _data: files, value: xmlFIle ? [xmlFIle.fileContent] : [] };
  }

  static async parseFileContent(
    data: Partial<Leaflet>,
    operation: OperationKeys = OperationKeys.CREATE
  ): Promise<Leaflet> {
    const { xmlFileContent } = data;
    data.otherFilesContent = [];
    const leafletId = data.id;
    function buildFileContent(fileName: string, fileContent: string): LeafletFile {
      const now = new Date();
      const file = Model.build(
        {
          leafletId,
          fileName,
          fileContent,

          id: `${leafletId}:${fileName}`,
          ...(operation === OperationKeys.CREATE ? { createdAt: now, updatedAt: now } : {}),
        },
        LeafletFile.name
      ) as LeafletFile;

      return file;
    }
    if (xmlFileContent) {
      const arrayFiles = JSON.parse(xmlFileContent as string) as { name: string; source: string }[];
      for (const file of arrayFiles) {
        const isXmlFile = file.name.toLowerCase().endsWith('.xml');
        const fileContent = buildFileContent(file.name, file.source);
        if (isXmlFile) {
          data.xmlFileContent = fileContent;
        } else {
          data.otherFilesContent.push(fileContent as string & LeafletFile);
        }
      }
    }
    if (!data.batchNumber?.length) {
      delete (data as KeyValue)['batchNumber'];
    }
    if (!data.epiMarket?.length || data.batchNumber?.length) {
      delete (data as KeyValue)['epiMarket'];
    }
    return data as Leaflet;
  }

  static async getDocumentType(instance: ListComponent, prop: string = 'leafletType', value: string): Promise<string> {
    const { operation } = instance;
    if (!operation) {
      return getDoucumentOptions().find((item) => value === item.value)?.text || '';
    }
    return 'subinfo';
  }

  static async getLanguage(instance: ListComponent, prop: string = 'lang', value: string): Promise<string> {
    const { operation } = instance;
    if (!operation) {
      return getLanguageOptions().find((item: SelectOption) => value === item.value)?.text || '';
    }
    return 'subinfo';
  }

  static async getMarket(instance: ListComponent, prop: string = 'epiMarket', value: string): Promise<string | void> {
    return getMarketOptions().find((item: SelectOption) => value === item.value)?.text || '';
  }

  static getSearchOptions(filter: string, value: string | null, condition: string): IFilterQuery {
    return {
      query: [
        {
          index: filter as string,
          condition,
          value,
        },
      ],
      sort: {
        value: filter,
        direction: OrderDirection.ASC,
      },
    };
  }

  static async parseSearchResult(result: KeyValue[] | Paginator<Model>): Promise<KeyValue[]> {
    const component = this as unknown as ListComponent;
    if (!Array.isArray(result) && 'page' in result && 'total' in result) {
      const paginator = result as Paginator<Model>;

      try {
        result = await paginator.page(component.page);
        component.getMoreData(paginator.total || component.pages);
      } catch (error: unknown) {
        component['log'].info(
          (error as Error)?.message || 'Unable to get page from paginator. Return empty array from component'
        );
        result = [];
      }
    } else {
      component.getMoreData((result as KeyValue[])?.length || 0);
    }

    // If is a batch route, make a second filter do get only results with the batch number
    if (component.route?.includes('batches')) {
      const batchNumber = String(component.modelId).split(':')[1];
      result = result.filter((item: Partial<Batch>) => item.batchNumber?.trim() === batchNumber.trim());
    }
    return Object.keys(component.mapper || {}).length ? await component.mapResults(result) : result;
  }
}
